/**
 * TextBlock component for rendering markdown content from Claude messages.
 *
 * Uses ReactMarkdown with custom components implementing the Charlie markdown
 * style guide (typography.css.ts) on top of Mantine Text styling.
 * Code elements are distinguished by className presence:
 * - Inline code (`code`): no className → styled as inline
 * - Code blocks (```lang```): has "language-{lang}" className → CodeBlock card
 *
 * Task lists (via remark-gfm) receive special classes:
 * - "contains-task-list" on <ul> elements (rendered as a surface card)
 * - "task-list-item" on <li> elements with checkboxes
 */

import { CSSProperties } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import { Text, VisuallyHidden } from '@mantine/core';
import { IconArrowUpRight } from '@tabler/icons-react';
import remarkDirective from 'remark-directive';
import remarkGfm from 'remark-gfm';
import { Cmp } from '~portal/lib';
import { typo } from '../typography.css';
import { ArtifactCard } from './ArtifactCard';
import { artifactCard } from './ArtifactCard.css';
import {
  artifactIdFrom,
  ArtifactImage,
  ArtifactLink,
  artifactUrlTransform,
  isArtifactCardList,
  isSoleImageParagraph,
  soleArtifactLink,
} from './ArtifactRef';
import { CodeBlock } from './CodeBlock';
import { FilesSummary } from './FilesSummary';
import { publishIntrinsicRatio } from './image-ratio';
import { ImageGallery } from './ImageGallery';
import { MdTable } from './MdTable';
import { rehypeGroupArtifactCards } from './rehype-group-artifact-cards';
import { rehypeRevealSections, REVEAL_INDEX_ATTRIBUTE } from './rehype-reveal-sections';
import { rehypeTableCaptions } from './rehype-table-captions';
import {
  FILES_DIRECTIVE_TAG,
  GALLERY_DIRECTIVE_TAG,
  remarkResponseDirectives,
} from './remark-response-directives';

export type MdContentProps = {
  text: string;
  'data-variant'?: string;
  /**
   * @desc Group the answer's blocks into heading-led sections and stagger their arrival.
   * Reserved for the agent's final answer: everything else in a turn — the reasoning
   * disclosure, markdown a tool emitted — is already on screen or is not the thing the user
   * is waiting for, and staggering it would animate the scaffolding rather than the answer.
   */
  reveal?: boolean;
};

/**
 * Hoisted so the plugin lists are referentially stable across renders. Order is meaningful:
 * captions run first, since rewriting a lead-in paragraph into a heading changes where the
 * sections are; the card grouping then folds runs of stand-alone file links into one block; and
 * the reveal, which groups top-level blocks by heading, sees the result of both.
 */
const PLUGINS = [rehypeTableCaptions, rehypeGroupArtifactCards];
const REVEAL_PLUGINS = [rehypeTableCaptions, rehypeGroupArtifactCards, rehypeRevealSections];

/**
 * `remarkDirective` parses the syntax; `remarkResponseDirectives` decides what the directives
 * the portal implements mean and puts every other one back as text. Both run after GFM, on the
 * same tree.
 */
const REMARK_PLUGINS = [remarkGfm, remarkDirective, remarkResponseDirectives];

export const MdContent: Cmp<MdContentProps> = function ({
  text,
  'data-variant': variant,
  reveal = false,
}) {
  return (
    <ReactMarkdown
      remarkPlugins={REMARK_PLUGINS}
      rehypePlugins={reveal ? REVEAL_PLUGINS : PLUGINS}
      // Widens the URL sanitiser by exactly one scheme, `artifact:` — the destination the agent
      // uses to reference a file it produced this turn (H2-206). The default drops it, taking
      // src/href with it, which is how an inline chart degraded to bare alt text.
      urlTransform={artifactUrlTransform}
      components={{
        /**
         * The response directives (ADR-23 layer 2): `::files` for the turn's deliverables and
         * `::gallery{ids="…"}` for a grid of named thumbnails, both rendered by the portal
         * rather than written out by the model. The keys are the `hName`s the directive plugin
         * sets, and the cast is because react-markdown's `Components` is keyed by the HTML tag
         * names it knows: a custom element is exactly what a directive produces, and there is
         * no other way to hand it a component.
         */
        ...({
          [FILES_DIRECTIVE_TAG]: () => <FilesSummary />,
          [GALLERY_DIRECTIVE_TAG]: ({ ids }: { ids?: string }) => <ImageGallery ids={ids} />,
        } as Components),
        strong: ({ children }) => (
          <Text component="strong" className={typo.md.strong} data-variant={variant}>
            {children}
          </Text>
        ),
        em: ({ children }) => (
          <em className={typo.md.em} data-variant={variant}>
            {children}
          </em>
        ),
        del: ({ children }) => (
          <del className={typo.md.del} data-variant={variant}>
            {children}
          </del>
        ),
        hr: () => <hr className={typo.md.hr} data-variant={variant} />,
        /**
         * Paragraphs, except one that is nothing but an artefact link: that is the file being
         * offered rather than mentioned, so it renders as a card (ADR-22 decision 4). The rule
         * is `where the link sits`, which only the renderer knows — the model writes the same
         * link either way.
         */
        p: ({ children, node }) => {
          const sole = soleArtifactLink(node);
          if (sole) return <ArtifactCard artifactId={sole.artifactId} label={sole.label} />;

          return (
            // A paragraph carrying nothing but a figure is rendered as a div: an image is a
            // block (and its loading skeleton is a div), and `<div>` inside `<p>` is invalid
            // markup — React reports it and the browser repairs it by closing the paragraph
            // early, which is a real layout change, not only a console warning.
            <Text
              component={isSoleImageParagraph(node) ? 'div' : 'p'}
              className={typo.md.p}
              data-variant={variant}
            >
              {children}
            </Text>
          );
        },
        h1: ({ children }) => (
          <Text component="h1" className={typo.md.h1} data-variant={variant}>
            {children}
          </Text>
        ),
        h2: ({ children, className }) =>
          className?.includes('sr-only') ? (
            <VisuallyHidden component="h2">{children}</VisuallyHidden>
          ) : (
            <Text component="h2" className={typo.md.h2} data-variant={variant}>
              {children}
            </Text>
          ),
        h3: ({ children }) => (
          <Text component="h3" className={typo.md.h3} data-variant={variant}>
            {children}
          </Text>
        ),
        h4: ({ children }) => (
          <Text component="h4" className={typo.md.h4} data-variant={variant}>
            {children}
          </Text>
        ),
        h5: ({ children }) => (
          <Text component="h5" className={typo.md.h5} data-variant={variant}>
            {children}
          </Text>
        ),
        h6: ({ children }) => (
          <Text component="h6" className={typo.md.h6} data-variant={variant}>
            {children}
          </Text>
        ),
        // Accent underlined links; external links get the ↗ affordance. An `artifact:` href is
        // not a link at all — it is a mention of a file only a signed URL can reach, so it
        // renders as an inline download card instead.
        a: ({ children, href }) => {
          const artifactId = artifactIdFrom(href);
          if (artifactId) return <ArtifactLink artifactId={artifactId}>{children}</ArtifactLink>;

          const isExternal = typeof href === 'string' && /^https?:\/\//.test(href);
          return (
            <a
              href={href}
              className={typo.md.a}
              target={isExternal ? '_blank' : undefined}
              rel={isExternal ? 'noopener noreferrer' : undefined}
              data-variant={variant}
            >
              {children}
              {isExternal && <IconArrowUpRight size={10} className={typo.md.linkIcon} />}
            </a>
          );
        },
        img: ({ src, alt }) => {
          const artifactId = artifactIdFrom(src);
          return artifactId ? (
            <ArtifactImage artifactId={artifactId} alt={alt ?? ''} />
          ) : (
            <img src={src} alt={alt ?? ''} className={typo.md.img} onLoad={publishIntrinsicRatio} />
          );
        },
        // Fenced blocks render as CodeBlock cards; the pre wrapper is dropped
        // so the card provides its own chrome
        pre: ({ children }) => <>{children}</>,
        code: ({ children, className }) => {
          const isInline = !className;
          if (isInline) {
            return (
              <Text component="code" className={typo.md.code.inline} data-variant={variant}>
                {children}
              </Text>
            );
          }
          const language = /language-(\w+)/.exec(className ?? '')?.[1];
          return (
            <CodeBlock
              code={String(children).replace(/\n$/, '')}
              language={language}
              data-variant={variant}
            />
          );
        },
        // Style blockquotes
        blockquote: ({ children }) => (
          <blockquote className={typo.md.blockquote} data-variant={variant}>
            {children}
          </blockquote>
        ),
        // Tables render inside a Charlie surface card with copy/download
        // controls; the hast node carries the cell values those exports use
        table: ({ children, node }) => (
          <MdTable node={node} data-variant={variant}>
            {children}
          </MdTable>
        ),
        th: ({ children }) => (
          <th className={typo.md.table.th} data-variant={variant}>
            {children}
          </th>
        ),
        td: ({ children }) => (
          <td className={typo.md.table.td} data-variant={variant}>
            {children}
          </td>
        ),
        // Style lists; GFM task lists keep their marker classes for the card
        // and checkbox treatments
        // A list of nothing but artefact links is laid out as a set of file cards and drops the
        // prose list's own class entirely — carrying both would leave the two fighting over the
        // gutter, decided by stylesheet order rather than by intent.
        ul: ({ children, className, node }) => (
          <Text
            component="ul"
            className={
              isArtifactCardList(node)
                ? artifactCard.list
                : `${typo.md.ul}${className ? ` ${className}` : ''}`
            }
            data-variant={variant}
          >
            {children}
          </Text>
        ),
        ol: ({ children, node }) => (
          <Text
            component="ol"
            className={isArtifactCardList(node) ? artifactCard.list : typo.md.ol}
            data-variant={variant}
          >
            {children}
          </Text>
        ),
        // A bullet that is nothing but an artefact link is a card, like the equivalent
        // paragraph — which is what turns the model's closing list of deliverables into a stack
        // of cards without any new syntax (ADR-23).
        li: ({ children, className, node }) => {
          const sole = soleArtifactLink(node);

          return (
            <Text
              component="li"
              className={
                sole ? artifactCard.listItem : `${typo.md.li}${className ? ` ${className}` : ''}`
              }
              data-variant={variant}
            >
              {sole ? <ArtifactCard artifactId={sole.artifactId} label={sole.label} /> : children}
            </Text>
          );
        },
        input: ({ checked, disabled, type }) =>
          type === 'checkbox' ? (
            <input
              type="checkbox"
              checked={checked}
              disabled={disabled}
              readOnly
              className={typo.md.taskCheckbox}
            />
          ) : null,
        // GFM footnotes section: right-aligned mdREF text under a rule. A reveal wrapper (see
        // rehype-reveal-sections) is the other section this renderer sees: it publishes its
        // position as a CSS variable and lets the stylesheet own the timing.
        section: ({ children, ...props }) => {
          if ('data-footnotes' in props)
            return (
              <section className={typo.md.footnotes} data-variant={variant}>
                {children}
              </section>
            );

          const { [REVEAL_INDEX_ATTRIBUTE]: revealIndex } = props as Record<string, string>;
          if (revealIndex === undefined) return <section>{children}</section>;

          return (
            <section
              className={typo.md.revealSection}
              data-reveal-index={revealIndex}
              style={{ '--reveal-index': revealIndex } as CSSProperties}
            >
              {children}
            </section>
          );
        },
      }}
    >
      {text}
    </ReactMarkdown>
  );
};
